package com.castly.admin.ui.preview

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * PreviewMyLikesView — displays all liked content with pagination.
 * Each item shows cover art, title, type badge, and an unlike button.
 * Tapping an item navigates to the detail page.
 */
@Composable
fun PreviewMyLikesView(
    apiClient: ApiClient,
    selectedLanguage: String,
    onContentClick: (com.castly.admin.dto.ContentResponse) -> Unit
) {
    val colors = AppTheme.colors
    var items by remember { mutableStateOf<List<com.castly.admin.dto.ContentResponse>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var loadingMore by remember { mutableStateOf(false) }
    var currentPage by remember { mutableStateOf(1) }
    var totalCount by remember { mutableStateOf(0) }
    val scope = rememberCoroutineScope()

    val hasMore = items.size < totalCount

    fun loadLikes(page: Int = 1, append: Boolean = false) {
        scope.launch {
            if (append) loadingMore = true else loading = true

            when (val result = apiClient.getLikedContent(
                page = page,
                pageSize = 20,
                language = selectedLanguage
            )) {
                is ApiClient.ApiResult.Success -> {
                    if (append) {
                        items = items + result.data.items
                    } else {
                        items = result.data.items
                    }
                    totalCount = result.data.total
                    currentPage = page
                }
                is ApiClient.ApiResult.Error -> {
                    console.error("Failed to load likes: ${result.message}")
                }
            }
            loading = false
            loadingMore = false
        }
    }

    fun unlikeItem(contentId: String) {
        // Optimistic removal
        val removedItem = items.find { it.id == contentId }
        items = items.filter { it.id != contentId }
        totalCount = (totalCount - 1).coerceAtLeast(0)

        scope.launch {
            when (apiClient.unlikeContent(contentId)) {
                is ApiClient.ApiResult.Error -> {
                    // Revert on error
                    if (removedItem != null) {
                        items = items + removedItem
                        totalCount += 1
                    }
                }
                is ApiClient.ApiResult.Success -> { /* already removed */ }
            }
        }
    }

    LaunchedEffect(selectedLanguage) {
        loadLikes()
    }

    Div({
        style {
            padding(Spacing.lg)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.md)
        }
    }) {
        if (loading) {
            // Loading state
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    padding(Spacing.xl)
                }
            }) {
                Span({ style { color(Color(colors.textMuted)); fontSize(Typography.base) } }) {
                    Text("Loading likes...")
                }
            }
        } else if (items.isEmpty()) {
            // Empty state
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    padding(Spacing.xl)
                    gap(Spacing.md)
                }
            }) {
                Span({ style { fontSize(40.px) } }) { Text("\u2661") } // ♡
                Span({
                    style {
                        color(Color(colors.textMuted))
                        fontSize(Typography.base)
                        fontWeight("500")
                    }
                }) { Text("No likes yet") }
                Span({
                    style {
                        color(Color(colors.textMuted))
                        fontSize(Typography.sm)
                        property("text-align", "center")
                    }
                }) { Text("Tap the heart icon on any content to add it to your likes") }
            }
        } else {
            // Likes count header
            Span({
                style {
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                    marginBottom(Spacing.xs)
                }
            }) { Text("$totalCount liked items") }

            // Content list
            items.forEach { content ->
                Div({
                    onClick { onContentClick(content) }
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.md)
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        property("transition", "background-color 0.15s ease")
                    }
                }) {
                    // Cover art
                    if (!content.coverImageUrl.isNullOrBlank()) {
                        Img(src = content.coverImageUrl, alt = content.title) {
                            style {
                                width(56.px)
                                height(56.px)
                                borderRadius(BorderRadius.sm)
                                property("object-fit", "cover")
                                property("flex-shrink", "0")
                            }
                        }
                    } else {
                        Div({
                            style {
                                width(56.px)
                                height(56.px)
                                borderRadius(BorderRadius.sm)
                                backgroundColor(Color(colors.surfaceHover))
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                property("flex-shrink", "0")
                            }
                        }) {
                            Span({ style { fontSize(Typography.xl); color(Color(colors.textMuted)) } }) {
                                Text(if (content.type == "podcast") "P" else "A")
                            }
                        }
                    }

                    // Title + metadata
                    Div({
                        style {
                            flex(1)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(Spacing.xs)
                            overflow("hidden")
                        }
                    }) {
                        Span({
                            style {
                                fontWeight("600")
                                fontSize(Typography.sm)
                                color(Color(colors.text))
                                property("white-space", "nowrap")
                                property("text-overflow", "ellipsis")
                                overflow("hidden")
                            }
                        }) { Text(content.title) }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(Spacing.sm)
                            }
                        }) {
                            // Type badge
                            Span({
                                style {
                                    fontSize(Typography.xs)
                                    padding(1.px, Spacing.sm)
                                    borderRadius(8.px)
                                    backgroundColor(Color(if (content.type == "podcast") "#dbeafe" else "#fce7f3"))
                                    color(Color(if (content.type == "podcast") "#1d4ed8" else "#be185d"))
                                    fontWeight("500")
                                    property("text-transform", "capitalize")
                                }
                            }) { Text(content.type) }

                            // Episode count
                            if (content.episodeCount > 0) {
                                Span({
                                    style {
                                        fontSize(Typography.xs)
                                        color(Color(colors.textMuted))
                                    }
                                }) { Text("${content.episodeCount} episodes") }
                            }
                        }
                    }

                    // Unlike button
                    Button({
                        onClick { e ->
                            e.stopPropagation()
                            unlikeItem(content.id)
                        }
                        style {
                            backgroundColor(Color.transparent)
                            border(0.px)
                            cursor("pointer")
                            fontSize(20.px)
                            padding(Spacing.xs)
                            lineHeight("1")
                        }
                    }) { Text("\u2764\uFE0F") } // ❤️
                }
            }

            // Load more button
            if (hasMore) {
                Button({
                    onClick { loadLikes(currentPage + 1, append = true) }
                    style {
                        width(100.percent)
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        fontSize(Typography.sm)
                        marginTop(Spacing.sm)
                    }
                }) { Text(if (loadingMore) "Loading..." else "Load More") }
            }
        }
    }
}
